import "./style.scss";

import { Col, Row, Spacer } from "@amzn/stencil-react-components/layout";
import { WithModal } from "@amzn/stencil-react-components/modal";
import {
  PageHeader,
  PageHeaderColorScheme,
  PageHeaderDropdown,
  PageHeaderDropdownButton,
  PageHeaderDropdownLink,
  PageHeaderLink,
  PageHeaderList,
} from "@amzn/stencil-react-components/page";
import { ToggleSwitch } from "@amzn/stencil-react-components/toggle-switch";
import { IconDarkMode, IconGrowth, IconLightMode, IconLiveHelp } from "@amzn/stencil-react-icons";
import IconQuestionCircle from "@amzn/stencil-react-icons/icons/icon-question-circle";
import SearchField from "@amzn/uxm-stencil-react-components/lib/src/stencil/search-field";
import { trackPageView } from "@snowplow/browser-tracker";
import { AiPolicyModalButton } from "common/components/PolicyModal/AiPolicyModalButton";
import SearchBoxFactory from "common/factories/search-box-factory";
import { trackUxmCustomEvent } from "common/helpers/analytics-helper";
import { Paths } from "common/paths/paths";
import { AiForMeURLQueryStrings } from "common/types";
import { getString } from "common/uistringlabels/uiStringUtils";
import { UserPermissions } from "lib/services";
import React, { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { ITMP_V2_ADMIN_URL, PERMISSIONS } from "../../../config";
import { ApprovalRequestsLink } from "../ApprovalRequestsLink";
import { CustomerFeedbackSheet } from "../CustomerFeedback/Sheet/CustomerFeedbackSheet";
import UserProfile from "../UserProfile";

const pathsWithoutSearchBar = ["^/$", "^/aiforme", `^\\${Paths.hardwareSearch.url}.+`];
const AI_FOR_ME_PATH = "/aiforme";
const EXTERNAL_LINKS = {
  FEATURE_REQUEST_URL: "https://w.amazon.com/bin/view/AIforMe#HAIforMeFeatureRequestForm",
  CONTACT_US_URL: "https://w.amazon.com/bin/view/AIforMe/ContactUs/",
  TOOL_ACCESS_ISSUES_URL: "https://w.amazon.com/bin/view/AIforMe#HAIforMetoolaccessissues",
};

export interface HeaderProps {
  toggleSearchExperience?: boolean;
  isDarkMode?: boolean;
  onDarkModeToggle?: () => void;
}

export const Header = (props: HeaderProps) => {
  const useHardwareShop = UserPermissions.current.hasPermission(PERMISSIONS.USE_HARDWARE_SHOP);
  const location = useLocation();
  const [isSearchbarVisible, setSearchbarVisible] = useState(false);
  const [headerItems, setHeaderItems] = useState<ReactNode[]>([]);

  const isAiForMe: boolean =
    location?.pathname === AI_FOR_ME_PATH || location?.pathname.startsWith(`${AI_FOR_ME_PATH}/`);
  const isAllAiToolsPage: boolean = location?.pathname.startsWith(Paths.aiForMeAllAiTools.url);

  useEffect(() => {
    getHeaderItems().then(setHeaderItems);
  }, [isAiForMe, props.isDarkMode, props.onDarkModeToggle]);

  const useQuery = () => {
    return new URLSearchParams(location?.search);
  };
  const queryValue = useQuery().get("query");

  const isPageWithoutSearchBar = (): boolean => {
    for (let i = 0; i < pathsWithoutSearchBar.length; i++) {
      const element = pathsWithoutSearchBar[i];
      const r = new RegExp(element);
      if (r.test(location?.pathname)) return true;
    }
    return false;
  };

  const toggleSearchBar = (): void => {
    setSearchbarVisible(!isPageWithoutSearchBar());
  };

  const trackUxmPageHeaderLinkEvent = (touchpoint: string) => {
    trackUxmCustomEvent(touchpoint, {});
  };

  /* istanbul ignore next */
  useEffect(() => {
    toggleSearchBar();
    if (location?.pathname) {
      trackPageView();
    }
  }, [location?.pathname]);

  const history = useHistory();

  // AI for Me search state - reads/writes URL params directly
  const [aiForMeSearchValue, setAiForMeSearchValue] = useState(() => {
    const params = new URLSearchParams(location?.search);
    return params.get(AiForMeURLQueryStrings.QUERY) || "";
  });

  const urlUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleAiForMeSearchChange = useCallback(
    (query: string) => {
      setAiForMeSearchValue(query);

      // Debounce URL update to prevent freezing
      if (urlUpdateTimeoutRef.current) clearTimeout(urlUpdateTimeoutRef.current);
      urlUpdateTimeoutRef.current = setTimeout(() => {
        const params = new URLSearchParams(window.location.search);
        if (query) {
          params.set(AiForMeURLQueryStrings.QUERY, query);
        } else {
          params.delete(AiForMeURLQueryStrings.QUERY);
        }
        history.replace(`${window.location.pathname}?${params.toString()}`);
      }, 300);
    },
    [history]
  );

  /* istanbul ignore next */
  const onLogoClickHander: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.preventDefault();
    history.push(isAiForMe ? AI_FOR_ME_PATH : "/");
  };

  // Header items
  const getHeaderItems = async () => {
    const items: ReactNode[] = [];

    if (!isAiForMe) {
      items.push(adminWebsiteButton());
      items.push(<ApprovalRequestsLink key="approval-requests" />);
      items.push(helpButton());
      items.push(faqButton());
      items.push(<UserProfile key="user-profile" username={UserPermissions.current.username} />);
    } else {
      items.push(
        <PageHeaderLink
          key="contribute-tool"
          href="/aiforme/contribute"
          onClick={() => {
            trackUxmPageHeaderLinkEvent("header-contribute-tool");
          }}
          icon={<IconGrowth />}
        >
          Contribute a tool
        </PageHeaderLink>
      );

      items.push(<AiPolicyModalButton key="ai-policy-button" />);

      items.push(
        <WithModal
          key="support-dropdown"
          renderModal={({ close }) => <CustomerFeedbackSheet touchpoint="aiforme-header" close={close} />}
        >
          {({ open }) => (
            <PageHeaderDropdown
              icon={<IconLiveHelp />}
              items={[
                <PageHeaderDropdownLink
                  key="feature-request"
                  href={EXTERNAL_LINKS.FEATURE_REQUEST_URL}
                  onClick={() => {
                    trackUxmPageHeaderLinkEvent("header-feature-request");
                  }}
                  target="_blank"
                >
                  {getString("header.featureRequest")}
                </PageHeaderDropdownLink>,
                <PageHeaderDropdownLink
                  key="contact-us"
                  href={EXTERNAL_LINKS.CONTACT_US_URL}
                  onClick={() => {
                    trackUxmPageHeaderLinkEvent("header-contact-us");
                  }}
                  target="_blank"
                >
                  {getString("header.reportDefects")}
                </PageHeaderDropdownLink>,
                <PageHeaderDropdownLink
                  key="tool-access-issues"
                  href={EXTERNAL_LINKS.TOOL_ACCESS_ISSUES_URL}
                  onClick={() => {
                    trackUxmPageHeaderLinkEvent("header-tool-access-issues");
                  }}
                  target="_blank"
                >
                  {getString("header.toolAccessIssues")}
                </PageHeaderDropdownLink>,
                <PageHeaderDropdownButton
                  key="share-feedback"
                  data-testid="share-feedback-button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    open();
                  }}
                >
                  {getString("header.shareFeedback")}
                </PageHeaderDropdownButton>,
              ]}
            >
              {getString("header.support")}
            </PageHeaderDropdown>
          )}
        </WithModal>
      );
      // Remove comment when we have the correct feature lists
      // items.push(
      //   <PageHeaderLink
      //     key="whatsNew"
      //     onClick={() => {
      //       trackUxmPageHeaderLinkEvent("header-whats-new");
      //       history.push(Paths.whatsNew.url);
      //     }}
      //     target="_blank"
      //   >
      //     {getString("aiForMe.whatsNew.title")}
      //   </PageHeaderLink>
      // );
      items.push(<UserProfile key="user-profile-ai" username={UserPermissions.current.username} />);
      if (props.onDarkModeToggle) {
        items.push(
          <Row key="dark-mode-toggle" alignItems="center" columnGap={8}>
            <IconLightMode />
            <ToggleSwitch
              aria-label={getString("aiForMe.toggleDarkMode")}
              title={getString("aiForMe.toggleDarkMode")}
              checked={props.isDarkMode || false}
              onChange={props.onDarkModeToggle}
            />
            <IconDarkMode />
          </Row>
        );
      }
    }

    return items;
  };

  const adminWebsiteButton = () => {
    if (UserPermissions.current.hasPermission(PERMISSIONS.SOFTWARE_PROCUREMENT)) {
      return (
        <PageHeaderLink
          key="1"
          href={ITMP_V2_ADMIN_URL}
          data-cy="admin-link"
          onClick={() => {
            trackUxmPageHeaderLinkEvent("header-admin");
          }}
          className="header-primary-text admin-link"
        >
          {getString("header.admin")}
        </PageHeaderLink>
      );
    }
  };

  const helpButton = () => {
    if (useHardwareShop) {
      return (
        <PageHeaderDropdown
          key="help-dropdown"
          className="header-primary-text"
          dataTestId="support-dropdown"
          data-cy="support-dropdown"
          icon={<IconQuestionCircle />}
          items={[
            <PageHeaderDropdownLink
              key="softwareSupport"
              href="https://w.amazon.com/bin/view/ITMarketplaceSoftwareOrdering"
              target="_blank"
              dataTestId="software-support"
              onClick={() => {
                trackUxmPageHeaderLinkEvent("header-software-support");
              }}
            >
              {getString("header.softwareSupport")}
            </PageHeaderDropdownLink>,
            <PageHeaderDropdownLink
              key="hardwareSupport"
              href={`${Paths.orders.url}?flyoutHardwareSupport=true`}
              dataTestId="hardware-support"
              onClick={() => {
                trackUxmPageHeaderLinkEvent("header-hardware-support");
              }}
            >
              {getString("header.hardwareSupport")}
            </PageHeaderDropdownLink>,
          ]}
        ></PageHeaderDropdown>
      );
    }
    return (
      <PageHeaderLink
        key="help-link"
        aria-label={getString("header.helpAriaLabel")}
        data-cy="help-link"
        href="https://w.amazon.com/bin/view/ITMarketplaceSoftwareOrdering"
        onClick={() => {
          trackUxmPageHeaderLinkEvent("header-help");
        }}
        className="header-primary-text icon help"
        target="_blank"
      />
    );
  };

  const faqButton = () => {
    return (
      <PageHeaderLink
        key="faq-link"
        href={Paths.faq.url}
        data-cy="faq-link"
        onClick={() => {
          trackUxmPageHeaderLinkEvent("header-faq");
        }}
        className="header-primary-text faq-link"
      >
        {getString("header.faqs")}
      </PageHeaderLink>
    );
  };

  return (
    <PageHeader colorScheme={PageHeaderColorScheme.Default} hasBottomBorder isFixed={true} isFullWidth={false}>
      <nav aria-label="Site" className="site-navigation">
        {/* IT Shop link */}
        <PageHeaderLink
          fontSize={"T400"}
          fontWeight={"bold"}
          color={"neutral90"}
          href={isAiForMe ? AI_FOR_ME_PATH : "/"}
          data-cy="logo-link"
          onClickCapture={onLogoClickHander}
        >
          {!isAiForMe && getString("logoText")}
          {isAiForMe && getString("aiForMe.title")}
        </PageHeaderLink>
        <Spacer flex={1} />

        {/* Search bar */}
        {isSearchbarVisible && (
          <Col width="30%" alignItems="center" className="search-box">
            <SearchBoxFactory
              queryText={queryValue}
              touchpointId="search-box-header"
              toggleSearchExperience={props.toggleSearchExperience ?? false}
            />
          </Col>
        )}
        {isAllAiToolsPage && (
          <Col width="30%" alignItems="center" className="search-box">
            <SearchField
              type="text"
              data-testid="txt_search_aiforme"
              onChange={handleAiForMeSearchChange}
              touchpoint="search-ai-tools-header"
              placeholder="Search"
              shouldShowListOnNoResults={false}
              query={aiForMeSearchValue}
            />
          </Col>
        )}
        <Spacer flex={1} />

        {/* Top right items */}
        <PageHeaderList data-cy="header-items" items={headerItems} />
      </nav>
    </PageHeader>
  );
};
